import React, { useState, useEffect, Fragment } from 'react';
import { DayPicker } from 'react-day-picker';
import { CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import 'react-day-picker/dist/style.css';

// Helper function to get due date styling (matches SortableTaskItem logic)
const getDueDateStyle = (dueDate: Date) => {
  const today = new Date();
  const due = new Date(dueDate);
  
  // Reset time parts for accurate day comparison
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  
  const timeDiff = due.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  if (daysDiff < 0) {
    // Overdue - red (inverted colors)
    return { bg: 'bg-red-700 dark:bg-red-300', text: 'text-red-100 dark:text-red-900', isColored: true };
  } else if (daysDiff === 0) {
    // Due today - red (inverted colors)
    return { bg: 'bg-red-700 dark:bg-red-300', text: 'text-red-100 dark:text-red-900', isColored: true };
  } else if (daysDiff <= 2) {
    // Within 2 days - yellow
    return { bg: 'bg-yellow-500', text: 'text-white dark:text-yellow-900', isColored: true };
  } else {
    // More than 2 days - gray (normal text, no badge styling)
    return { bg: '', text: 'text-gray-700 dark:text-gray-300', isColored: false };
  }
};

interface DueDateSelectorProps {
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
}

const DueDateSelector: React.FC<DueDateSelectorProps> = ({
  selectedDate,
  onChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const [displayedMonth, setDisplayedMonth] = useState<Date>(new Date());

  // Update input value when pendingDate changes
  useEffect(() => {
    if (pendingDate) {
      const month = String(pendingDate.getMonth() + 1).padStart(2, '0');
      const day = String(pendingDate.getDate()).padStart(2, '0');
      const year = pendingDate.getFullYear();
      setInputValue(`${month}/${day}/${year}`);
    } else {
      setInputValue('');
    }
  }, [pendingDate]);

  // Initialize pending date and displayed month when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setPendingDate(selectedDate);
      setDisplayedMonth(selectedDate || new Date());
      if (selectedDate) {
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const year = selectedDate.getFullYear();
        setInputValue(`${month}/${day}/${year}`);
      } else {
        setInputValue('');
      }
    }
  }, [isModalOpen, selectedDate]);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Parse MM/DD/YYYY format
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(datePattern);
    
    if (match) {
      const month = parseInt(match[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      
      const date = new Date(year, month, day);
      
      // Validate the date
      if (
        date.getMonth() === month &&
        date.getDate() === day &&
        date.getFullYear() === year
      ) {
        setPendingDate(date);
        setDisplayedMonth(date);
      }
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setPendingDate(date);
    }
  };

  const handlePresetClick = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setPendingDate(date);
    setDisplayedMonth(date);
  };

  const handleRemoveDate = () => {
    setPendingDate(null);
  };

  const handleDone = () => {
    onChange(pendingDate);
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Conditional Due Date Button - show icon when nothing selected, show label when date selected */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className={selectedDate 
          ? `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getDueDateStyle(selectedDate).bg} ${getDueDateStyle(selectedDate).text}` 
          : "p-1 text-gray-400 dark:text-dark-secondary hover:text-gray-600 dark:hover:text-dark-primary focus:outline-none flex-shrink-0"
        }
        aria-label={selectedDate ? `Change due date from ${selectedDate.toLocaleDateString()}` : "Select due date"}
        title={selectedDate ? selectedDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          ...(selectedDate.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
        }) : undefined}
      >
        {selectedDate ? (
          `Due: ${selectedDate.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            ...(selectedDate.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
          })}`
        ) : (
          <CalendarIcon className="h-5 w-5" />
        )}
      </button>

      {/* Due Date Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[1100]"
          onClose={() => setIsModalOpen(false)}
        >
          {/* Backdrop removed - no background darkening */}

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-[22rem] transform overflow-hidden rounded-2xl bg-white dark:bg-dark-card p-6 text-left align-middle shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-primary"
                    >
                      Select Due Date
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="text-gray-400 dark:text-dark-secondary hover:text-gray-500 dark:hover:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-1"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Quick Presets */}
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => handlePresetClick(0)}
                      className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetClick(1)}
                      className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Tomorrow
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetClick(7)}
                      className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Next Week
                    </button>
                  </div>

                  {/* Date Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date (MM/DD/YYYY)
                    </label>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder="MM/DD/YYYY"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm bg-white dark:bg-dark-card text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Calendar */}
                  <div className="calendar-container mb-4">
                    <DayPicker
                      mode="single"
                      selected={pendingDate || undefined}
                      onSelect={handleDateSelect}
                      month={displayedMonth}
                      onMonthChange={setDisplayedMonth}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3">
                    {pendingDate && (
                      <button
                        type="button"
                        onClick={handleRemoveDate}
                        className="px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        Remove Date
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleDone}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default DueDateSelector; 